import React from "react";

interface UseCasesProps {
  isVisible: boolean;
}

const UseCases: React.FC<UseCasesProps> = ({ isVisible }) => {
  const useCases = [
    {
      title: "Prevent OpenAI Cost Overruns",
      description: "A SaaS startup integrated GPT-4 for their chatbot. Without monitoring, their API bill jumped from $200 to $8,000 in one week due to a bug in production.",
      solution: "With Alertaio, they set a $500 daily limit and received instant alerts when usage spiked at $450. They caught the bug early and saved $7,500.",
      keyword: "OpenAI cost alerts",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    {
      title: "Catch API Usage Spikes Early",
      description: "A developer accidentally left a test script running overnight that called the ChatGPT API in an infinite loop, consuming tokens at an alarming rate.",
      solution: "Alertaio detected the anomalous usage pattern within 10 minutes and sent an urgent notification. The script was stopped before it could rack up thousands in charges.",
      keyword: "API usage spike detection",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      title: "Budget Control for Multiple Projects",
      description: "An agency manages AI integrations for 15 different clients. Tracking costs manually across multiple OpenAI accounts was time-consuming and error-prone.",
      solution: "They use Alertaio to set individual budgets for each client project. Automated alerts and centralized dashboards save 10+ hours per week and prevent budget overruns.",
      keyword: "Multi-project AI budget management",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      title: "Non-Technical Founder Protection",
      description: "A non-technical founder hired developers to build an AI-powered app. They had no visibility into API costs until receiving a $12,000 invoice.",
      solution: "With Alertaio's simple dashboard, they now monitor costs daily without technical knowledge. Budget alerts ensure they stay within their $1,500 monthly limit.",
      keyword: "AI cost monitoring for non-technical users",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
  ];

  return (
    <section id="use-cases" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Real-World Use Cases
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              See how teams are using Alertaio to prevent AI API overspending and maintain budget control.
            </p>
          </div>

          {/* Use Cases Grid - 2x2 Layout */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-6 lg:p-8 hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                {/* Icon and Title */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-600 to-blue-600 text-white rounded-xl">
                      {useCase.icon}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {useCase.title}
                    </h3>
                    <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-medium px-3 py-1 rounded-full">
                      {useCase.keyword}
                    </span>
                  </div>
                </div>

                {/* Problem */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                    The Problem
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {useCase.description}
                  </p>
                </div>

                {/* Solution */}
                <div className="mt-auto">
                  <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2">
                    How Alertaio Helped
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {useCase.solution}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
